import React, { useState } from 'react'
import { Download, Copy } from 'lucide-react'
import { copyWithToast } from '../../utils/clipboard'

interface IRTechClassificationDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  showToast?: (message: string) => void;
  compact?: boolean;
  showHeader?: boolean;
}

const IRTechClassificationDisplay: React.FC<IRTechClassificationDisplayProps> = ({
  result,
  theme = 'light',
  showToast,
  compact = false,
  showHeader = true
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})
  const isDarkMode = theme === 'dark'

  // Helper function to copy text to clipboard
  const copyToClipboard = (text: string) => copyWithToast(text, showToast);

  // Parse JSON if result is a string
  let parsedResult = result
  if (typeof result === 'string') {
    try {
      parsedResult = JSON.parse(result)
    } catch (e) {
      // If parsing fails, show raw string
      return null;
    }
  }

  // Handle both direct result and nested output structure
  const classification = parsedResult?.output || parsedResult

  // Check if this is technology classification (has typical classification fields)
  const isTechClassification = classification && typeof classification === 'object' && (
    classification.primary_classification ||
    classification.secondary_classifications ||
    classification.technology_domain ||
    classification.application_areas ||
    classification.technical_disciplines ||
    classification.industry_sectors ||
    classification.patent_classes
  );

  if (!isTechClassification) {
    return null; // Not tech classification, let other components handle it
  }

  if (!classification || typeof classification !== 'object') {
    return null;
  }

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }))
  }

  const sectionConfigs = {
    primary: { 
      title: 'Primary Classification', 
      emoji: '🎯', 
      fields: ['primary_classification'] 
    },
    secondary: { 
      title: 'Secondary Classifications', 
      emoji: '📚', 
      fields: ['secondary_classifications'] 
    },
    domain: { 
      title: 'Technology Domain', 
      emoji: '🔬', 
      fields: ['technology_domain'] 
    },
    applications: { 
      title: 'Application Areas', 
      emoji: '🎨', 
      fields: ['application_areas'] 
    },
    disciplines: { 
      title: 'Technical Disciplines', 
      emoji: '🧠', 
      fields: ['technical_disciplines'] 
    },
    industries: { 
      title: 'Industry Sectors', 
      emoji: '🏭', 
      fields: ['industry_sectors'] 
    },
    patent_classes: { 
      title: 'Patent Classes', 
      emoji: '📋', 
      fields: ['patent_classes'] 
    }
  }

  const exportToDocx = () => {
    try {
      let docContent = `
        <html>
          <head>
            <title>Technology Classification</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
              h1 { color: #333; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; font-size: 18px; }
              h2 { color: #666; margin-top: 30px; margin-bottom: 15px; border-bottom: 1px solid #ddd; padding-bottom: 5px; font-size: 16px; }
              h3 { color: #333; margin-top: 20px; margin-bottom: 10px; font-size: 14px; }
              .section { margin: 25px 0; }
              .field-value { margin: 10px 0; }
              ul { margin: 8px 0; padding-left: 20px; }
              li { margin: 4px 0; }
              .separator { border-top: 3px solid #ddd; margin: 30px 0; }
              .emoji { font-size: 18px; margin-right: 8px; }
              .classification-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 20px 0; }
              .classification-item { background-color: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #6f42c1; }
            </style>
          </head>
          <body>
            <h1>Technology Classification</h1>
      `;

      // Generate sections
      Object.entries(sectionConfigs).forEach(([sectionKey, config]) => {
        const hasData = config.fields.some(field => classification[field] && (Array.isArray(classification[field]) ? classification[field].length > 0 : true));
        if (!hasData) return;

        docContent += `<div class="section">`;
        docContent += `<h2>${config.title}</h2>`;
        
        config.fields.forEach(field => {
          const value = classification[field];
          if (!value || (Array.isArray(value) && value.length === 0)) return;

          const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          
          if (Array.isArray(value)) {
            if (value.length > 0) {
              docContent += `<div class="classification-item">`;
              docContent += `<h3>${fieldLabel}</h3>`;
              docContent += '<ul>';
              value.forEach(item => {
                docContent += `<li>${typeof item === 'object' ? JSON.stringify(item) : item}</li>`;
              });
              docContent += '</ul>';
              docContent += `</div>`;
            }
          } else {
            docContent += `<div class="classification-item">`;
            docContent += `<h3>${fieldLabel}</h3>`;
            docContent += `<div class="field-value">${value}</div>`;
            docContent += `</div>`;
          }
        });
        
        docContent += `</div>`;
        docContent += `<div class="separator"></div>`;
      });

      docContent += '</body></html>';

      // Create and download the file
      const blob = new Blob([docContent], { type: 'application/msword' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'technology-classification.doc';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (showToast) {
        showToast('Technology Classification exported to Word document');
      }
    } catch (error) {
      console.error('Error exporting Technology Classification to DOCX:', error);
      if (showToast) {
        showToast('Error exporting Technology Classification');
      }
    }
  }

  const formatFieldValue = (field, value) => {
    if (!value) return 'Not specified'
    
    if (Array.isArray(value)) {
      if (value.length === 0) return 'None specified'
      return (
        <ul className="list-disc pl-5 mt-1 space-y-1">
          {value.map((item, index) => (
            <li key={index} className="leading-relaxed">
              {typeof item === 'object' ? JSON.stringify(item) : item}
            </li>
          ))}
        </ul>
      )
    }
    
    return value
  }

  const renderSection = (sectionKey, config) => {
    const hasData = config.fields.some(field => classification[field] && (Array.isArray(classification[field]) ? classification[field].length > 0 : true))
    if (!hasData) return null

    const isExpanded = expandedSections[sectionKey] !== false // Default to expanded

    return (
      <div key={sectionKey} className={`border rounded-lg ${isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        {/* Section Header */}
        <div 
          className={`flex items-center justify-between p-3 cursor-pointer ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
          onClick={() => toggleSection(sectionKey)}
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">{config.emoji}</span>
            <div>
              <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {config.title}
              </h3>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {config.fields.filter(field => classification[field] && (Array.isArray(classification[field]) ? classification[field].length > 0 : true)).length} field{config.fields.filter(field => classification[field] && (Array.isArray(classification[field]) ? classification[field].length > 0 : true)).length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {isExpanded ? '▲' : '▼'}
          </span>
        </div>

        {/* Section Content */}
        {isExpanded && (
          <div className={`px-3 pb-3 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            <div className="space-y-2">
              {config.fields.map(field => {
                const value = classification[field]
                if (!value || (Array.isArray(value) && value.length === 0)) return null

                const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())

                return (
                  <div key={field} className={`p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="flex items-start justify-between mb-1">
                      <span className={`text-xs font-medium px-2 py-1 rounded ${isDarkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800'}`}>
                        {fieldLabel}
                      </span>
                      <button
                        onClick={() => copyToClipboard(Array.isArray(value) ? value.join('\n') : value.toString())}
                        className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                    <div className={`text-xs leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      {formatFieldValue(field, value)}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Section Actions */}
            <div className="flex gap-2 mt-2 pt-2 border-t border-gray-300 dark:border-gray-600">
              <button
                onClick={() => {
                  const sectionText = config.fields
                    .filter(field => classification[field] && (Array.isArray(classification[field]) ? classification[field].length > 0 : true))
                    .map(field => {
                      const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                      const value = classification[field]
                      const formattedValue = Array.isArray(value) ? value.join('\n- ') : value
                      return `${fieldLabel}: ${formattedValue}`
                    }).join('\n\n');
                  copyToClipboard(sectionText);
                }}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                <Copy className="w-3 h-3" />
                Copy Section
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Expand/Collapse All Controls
  const allSections = Object.keys(sectionConfigs).filter(sectionKey => 
    sectionConfigs[sectionKey].fields.some(field => classification[field] && (Array.isArray(classification[field]) ? classification[field].length > 0 : true))
  )
  
  const allSectionsText = Object.entries(sectionConfigs)
    .filter(([sectionKey, config]) => 
      config.fields.some(field => classification[field] && (Array.isArray(classification[field]) ? classification[field].length > 0 : true))
    )
    .map(([sectionKey, config]) => {
      return `# ${config.title}\n\n${config.fields
        .filter(field => classification[field] && (Array.isArray(classification[field]) ? classification[field].length > 0 : true))
        .map(field => {
          const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
          const value = classification[field]
          const formattedValue = Array.isArray(value) ? '- ' + value.join('\n- ') : value
          return `## ${fieldLabel}\n\n${formattedValue}`
        }).join('\n\n')}`
    })
    .join('\n\n---\n\n')

  return (
    <div className="space-y-2 overflow-x-hidden">
      {showHeader && (
        <>
          {/* Header */}
          <div className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-50'}`}>
            <div className="flex items-center gap-2">
              <span className="text-xl">🔬</span>
              <div>
                <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Technology Classification
                </h2>
                <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {classification.primary_classification || 'Classification analysis results'}
                </p>
              </div>
            </div>

            {/* Global Actions */}
            <div className="flex gap-2">
              <button
                onClick={exportToDocx}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-green-700 hover:bg-green-600 text-green-100' : 'bg-green-500 hover:bg-green-600 text-white'}`}
                title="Export technology classification as Word document"
              >
                <Download className="w-3 h-3" />
                Export DOCX
              </button>
              <button
                onClick={() => copyToClipboard(allSectionsText)}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                <Copy className="w-3 h-3" />
                Copy All
              </button>
            </div>
          </div>

          {/* Expand/Collapse All Controls */}
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                const allExpanded = {}
                allSections.forEach(sectionKey => {
                  allExpanded[sectionKey] = true
                })
                setExpandedSections(allExpanded)
              }}
              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              Expand All
            </button>
            <button
              onClick={() => {
                const allCollapsed = {}
                allSections.forEach(sectionKey => {
                  allCollapsed[sectionKey] = false
                })
                setExpandedSections(allCollapsed)
              }}
              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              Collapse All
            </button>
          </div>
        </>
      )}

      {/* Sections */}
      <div className={`${compact ? "space-y-2" : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"}`}>
        {Object.entries(sectionConfigs).map(([sectionKey, config]) => renderSection(sectionKey, config))}
      </div>
    </div>
  )
}

export default IRTechClassificationDisplay